/* eslint-disable react-hooks/exhaustive-deps */
import jwtDecode from "jwt-decode";
import { useState, useEffect, useCallback, useRef } from "react";
import {
  getCurrentSubscriptionId,
  getAccessToken,
  getTokenForSubscriptionSSO,
  getIDToken,
  getRefreshToken,
  getTokenForProjectSSO
} from "services/auth";
import { getGraphQLClient } from "services/graphQL";
import { authHeader, serializeTokenExchange } from "services/token-refresh";
import {
  clearGqlExchangeCache,
  registerGqlExchangeCacheClearer
} from "utils/gql-exchange-cache";

const MIN_TOKEN_REFRESH_DELAY_MS = 60_000;
const PROJECT_TOKEN_REFRESH_LEAD_MS = 60_000;
const SUBSCRIPTION_TOKEN_REFRESH_LEAD_MS = 90_000;

/** One in-flight exchange per project id (survives Strict Mode remount). */
const projectExchangeById = new Map<string, Promise<void>>();
/** One in-flight exchange per subscription id (survives Strict Mode remount). */
const subscriptionExchangeById = new Map<
  string,
  Promise<{ exp: number | null } | null>
>();

type SubscriptionExchangeCacheEntry = {
  graphQlInstance: ReturnType<typeof getGraphQLClient>;
  gqlClient: ReturnType<typeof getGraphQLClient>["GQLClient"];
  wsLink: ReturnType<typeof getGraphQLClient>["wsLink"];
};

/** Completed subscription exchange (survives remount after in-flight dedup). */
const subscriptionExchangeResultById = new Map<
  string,
  SubscriptionExchangeCacheEntry
>();

function getCachedSubscriptionExchange(subscriptionId: string) {
  return subscriptionExchangeResultById.get(subscriptionId);
}

function cacheSubscriptionExchange(
  subscriptionId: string,
  graphQlIns: ReturnType<typeof getGraphQLClient>
): SubscriptionExchangeCacheEntry {
  const entry: SubscriptionExchangeCacheEntry = {
    graphQlInstance: graphQlIns,
    gqlClient: graphQlIns.GQLClient,
    wsLink: graphQlIns.wsLink
  };
  subscriptionExchangeResultById.set(subscriptionId, entry);
  return entry;
}

function scheduleTokenRefresh(
  expMs: number,
  leadTimeMs: number,
  onRefresh: () => void
): ReturnType<typeof setTimeout> {
  const expiringIn = expMs - leadTimeMs - Date.now();
  return setTimeout(
    onRefresh,
    Math.max(expiringIn, MIN_TOKEN_REFRESH_DELAY_MS)
  );
}

registerGqlExchangeCacheClearer(() => {
  projectExchangeById.clear();
  subscriptionExchangeById.clear();
  subscriptionExchangeResultById.clear();
});

/** Clears in-flight exchange cache (tests only). */
export function clearGqlExchangeCacheForTests(): void {
  clearGqlExchangeCache();
}

export const useGetGQLClientForProject = (projectId?: string) => {
  const [idValues, setIdValues] = useState<{
    projectId?: string;
  }>({
    projectId
  });
  const [isAboutToExpired, setIsAboutToExpired] = useState(true);
  const [gqlClientForProject, setGqlClientForProject] = useState<any>(null);
  const [projectToken, setProjectToken] = useState<any>(null);
  const [wsLink, setwsLink] = useState<any>(null);
  const [tokenRetrievalState, setTokenRetrievalState] = useState<any>({
    inProgress: false,
    success: null,
    error: false,
    msg: "",
    token: null
  });
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const projectTokenExpRef = useRef<number | null>(null);

  const disposeSubscription = useCallback(() => {
    try {
      if (wsLink) {
        wsLink.stop();
        wsLink.subscriptionClient.close();

        setwsLink(null);
      }
    } catch {
      //
    }
  }, [wsLink]);

  useEffect(() => {
    const runProjectExchange = async (activeProjectId: string) => {
      setIsAboutToExpired(false);
      setTokenRetrievalState({
        inProgress: true,
        success: null,
        error: false,
        msg: "",
        token: null
      });

      const idToken = getIDToken();
      const refreshToken = getRefreshToken();
      const setTokenResponse = await serializeTokenExchange(() =>
        getTokenForProjectSSO({
          projectId: activeProjectId,
          idToken: idToken!,
          refreshToken: refreshToken!
        })
      );
      const token = setTokenResponse?.data?.tokens?.AccessToken;

      if (token) {
        setProjectToken(token);
        const graphQlInstance = getGraphQLClient({
          token
        });
        setGqlClientForProject(graphQlInstance.GQLClient);
        setwsLink(graphQlInstance.wsLink);
        setTokenRetrievalState({
          inProgress: false,
          success: true,
          error: false,
          msg: "",
          token
        });
        const jwt: any = jwtDecode(token);
        const exp = (jwt && jwt.exp && jwt.exp * 1000) || null;
        projectTokenExpRef.current = exp;
        if (exp) {
          if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
          }
          refreshTimeoutRef.current = scheduleTokenRefresh(
            exp,
            PROJECT_TOKEN_REFRESH_LEAD_MS,
            () => setIsAboutToExpired(true)
          );
        }
        return;
      }

      setTokenRetrievalState({
        inProgress: false,
        success: false,
        error: true,
        msg: (setTokenResponse.error as any)?.message || ""
      });
    };

    const activeProjectId = idValues.projectId;
    if (activeProjectId && isAboutToExpired) {
      disposeSubscription();

      let exchange = projectExchangeById.get(activeProjectId);
      if (!exchange) {
        exchange = runProjectExchange(activeProjectId).finally(() => {
          if (projectExchangeById.get(activeProjectId) === exchange) {
            projectExchangeById.delete(activeProjectId);
          }
        });
        projectExchangeById.set(activeProjectId, exchange);
      }
      void exchange;
    }

    return () => {
      disposeSubscription();
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, [idValues, isAboutToExpired]);

  // Background tabs throttle (or freeze) timers, so the proactive refresh
  // timeout can fire long after the token expired. Re-check expiry on wake,
  // mirroring installTokenRefreshOnWake for the subscription token.
  useEffect(() => {
    const onWake = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      const exp = projectTokenExpRef.current;
      if (exp && Date.now() >= exp - PROJECT_TOKEN_REFRESH_LEAD_MS) {
        setIsAboutToExpired(true);
      }
    };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    return () => {
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
    };
  }, []);

  return {
    gqlClientForProject,
    tokenRetrievalState,
    projectToken,
    setIdValues
  };
};

export const useGetGQLClientForSubscription = () => {
  const subscriptionId = getCurrentSubscriptionId();
  const cachedExchange = subscriptionId
    ? getCachedSubscriptionExchange(subscriptionId)
    : undefined;
  const [gqlClient, setGqlClient] = useState<any>(cachedExchange?.gqlClient);
  const [wsLink, setwsLink] = useState<any>(cachedExchange?.wsLink);
  const [exchangeFailed, setExchangeFailed] = useState(false);
  const [isAboutToExpired, setIsAboutToExpired] = useState(!cachedExchange);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const applySubscriptionExchangeResult = useCallback(
    (entry: SubscriptionExchangeCacheEntry) => {
      setGqlClient(entry.gqlClient);
      setwsLink(entry.wsLink);
      setIsAboutToExpired(false);
    },
    []
  );

  const runSubscriptionExchange = useCallback(
    async (activeSubscriptionId: string) => {
      subscriptionExchangeResultById.delete(activeSubscriptionId);
      const idToken = getIDToken();
      const refreshToken = getRefreshToken();
      const subscriptionResponse: any = await serializeTokenExchange(() =>
        getTokenForSubscriptionSSO({
          subscriptionId: activeSubscriptionId,
          idToken: idToken!,
          refreshToken: refreshToken!
        })
      );
      const token = subscriptionResponse?.data?.tokens?.AccessToken;
      if (!subscriptionResponse?.success || !token) {
        subscriptionExchangeResultById.delete(activeSubscriptionId);
        setExchangeFailed(true);
        return null;
      }

      // Subscription client resolves auth via the coordinator so its token
      // (accToken) stays fresh reactively, including after a frozen/slept tab.
      const graphQlIns = getGraphQLClient({ getAuthHeaders: authHeader });
      cacheSubscriptionExchange(activeSubscriptionId, graphQlIns);

      const jwt: any = jwtDecode(token);
      const exp = (jwt?.exp && jwt.exp * 1000) || null;
      return { exp };
    },
    []
  );

  useEffect(() => {
    const accessToken = getAccessToken();
    if (accessToken && !subscriptionId) {
      const redirectTarget = `${window.location.pathname}${window.location.search}`;
      const redirectQuery =
        redirectTarget && redirectTarget !== "/"
          ? `?redirectUrl=${encodeURIComponent(redirectTarget)}`
          : "";
      window.location.replace(`/subscription-select${redirectQuery}`);
      return;
    }

    if (!subscriptionId || !isAboutToExpired) {
      return;
    }

    const cachedResult = getCachedSubscriptionExchange(subscriptionId);
    if (cachedResult) {
      applySubscriptionExchangeResult(cachedResult);
      return;
    }

    let cancelled = false;

    let exchange = subscriptionExchangeById.get(subscriptionId);
    if (!exchange) {
      const pendingExchange = runSubscriptionExchange(subscriptionId).finally(
        () => {
          if (
            subscriptionExchangeById.get(subscriptionId) === pendingExchange
          ) {
            subscriptionExchangeById.delete(subscriptionId);
          }
        }
      );
      subscriptionExchangeById.set(subscriptionId, pendingExchange);
      exchange = pendingExchange;
    }

    void exchange.then((exchangeResult) => {
      if (cancelled) {
        return;
      }

      const result = getCachedSubscriptionExchange(subscriptionId);
      if (result) {
        applySubscriptionExchangeResult(result);
      }

      const exp = exchangeResult?.exp;
      if (exp) {
        if (refreshTimeoutRef.current) {
          clearTimeout(refreshTimeoutRef.current);
        }
        refreshTimeoutRef.current = scheduleTokenRefresh(
          exp,
          SUBSCRIPTION_TOKEN_REFRESH_LEAD_MS,
          () => setIsAboutToExpired(true)
        );
      }
    });

    return () => {
      cancelled = true;
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, [
    subscriptionId,
    isAboutToExpired,
    runSubscriptionExchange,
    applySubscriptionExchangeResult
  ]);

  return { gqlClient, wsLink, exchangeFailed };
};
